import { CommonModule } from '@angular/common';
import { afterNextRender, ChangeDetectionStrategy, Component, computed, DestroyRef, effect, EventEmitter, inject, Input, Output, signal } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { AlertModule, ButtonModule, CardModule, ColComponent, ProgressModule, RowComponent, SpinnerModule } from '@coreui/angular-pro';
import { IconModule } from '@coreui/icons-angular';
import { QRCodeComponent } from 'angularx-qrcode';
import { ConfigService } from 'app';
import { TranslateService, TranslationModule } from 'i18n';
import { interval, switchMap, takeWhile } from 'rxjs';
import { LocaleNumberPipe, Logger, Platform } from 'shared';
import { OrderService } from '../../../../../../services/order.service';
import { OrderWizardTracking, confirmationErrorCategory } from '../../../order-wizard-tracking';
import { Web3ConnectionState, Web3Service } from '../../../../../../services/web3.service';
import { InAppBrowser } from '@ionic-native/in-app-browser/ngx';

interface PaymentState {
  step: 'connecting' | 'checking_balance' | 'sending' | 'confirming' | 'completed' | 'error';
  message: string;
  transactionHash: string | null;
  confirmations: number;
  requiredConfirmations: number;
  error: string | null;
}

@Component({
  selector: 'app-web3-confirmation',
  standalone: true,
  imports: [
    CommonModule,
    ButtonModule,
    CardModule,
    AlertModule,
    ProgressModule,
    SpinnerModule,
    QRCodeComponent,
    IconModule,
    TranslationModule,
    LocaleNumberPipe,
    RowComponent,
    ColComponent
  ],
  templateUrl: './web3-confirmation.component.html',
  styleUrls: ['./web3-confirmation.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class Web3ConfirmationComponent {
  @Input() orderId: number | null = null;
  @Input() totalPrice: number = 0;
  @Input() recipientAddress: string = '';
  @Output() onSuccess = new EventEmitter<any>();
  @Output() onBack = new EventEmitter<void>();

  private web3 = inject(Web3Service);
  private orderService = inject(OrderService);
  private translate = inject(TranslateService);
  private configService = inject(ConfigService);
  private platform = inject(Platform);
  private inAppBrowser = inject(InAppBrowser);
  private destroyRef = inject(DestroyRef);
  private tracking = inject(OrderWizardTracking, { optional: true });
  protected logger = new Logger('Web3ConfirmationComponent');

  // ────────────────────────────────────────────────
  // Reactive state
  // ────────────────────────────────────────────────
  connectionState = toSignal(this.web3.connectionState$, {initialValue: this.web3.getConnectionState()})
  paymentState = signal<PaymentState>({
    step: 'connecting',
    message: '',
    transactionHash: null,
    confirmations: 0,
    requiredConfirmations: 21,
    error: null
  });

  loading = signal<boolean>(false);
  qrCodeConnection = signal<boolean | null>(null);
  isMobile = signal<boolean>(this.platform.is('mobile'));
  pollInterval = signal<number>(15000); // default fallback

  // Which BSC stablecoin the customer pays in. Selectable before sending; the
  // choice drives the balance shown, the transfer contract and backend verify.
  readonly tokenOptions: Array<'USDT' | 'USDC'> = ['USDT', 'USDC'];
  selectedToken = signal<'USDT' | 'USDC'>('USDT');
  // Balance of the *selected* token (connectionState().balance is USDT-only).
  tokenBalance = signal<string | null>(null);

  // Computed helpers
  hasBrowserWallet = computed(() => this.web3.isWeb3Available());
  isCorrectNetwork = computed(() => this.connectionState().chainId === 56);
  hasSufficientBalance = computed(() => {
    const bal = this.tokenBalance();
    return bal != null ? parseFloat(bal) >= this.totalPrice : false;
  });
  // Lock the token selector once a transaction is under way.
  tokenLocked = computed(() =>
    this.loading() || ['sending', 'confirming', 'completed'].includes(this.paymentState().step)
  );

  canSendPayment = computed(() => 
    this.connectionState().connected &&
    this.isCorrectNetwork() &&
    this.hasSufficientBalance() &&
    !this.loading() &&
    !['sending', 'confirming'].includes(this.paymentState().step)
  );

  paymentButtonText = computed(() => {
    const step = this.paymentState().step;
    if (step === 'sending') return this.translate.translate('web3_sending');
    if (step === 'confirming') return this.translate.translate('web3_confirming');
    if (!this.hasSufficientBalance()) return this.translate.translate('web3_insufficient_button');
    return this.translate.translate('web3_send_payment', { amount: this.totalPrice, token: this.selectedToken() });
  });

  confirmationProgress = computed(() => {
    const { confirmations, requiredConfirmations } = this.paymentState();
    return (confirmations / requiredConfirmations) * 100;
  });

  progressColor = computed(() => {
    const progress = this.confirmationProgress();
    if (progress >= 100) return 'success';
    if (progress >= 75) return 'info';
    if (progress >= 50) return 'warning';
    return 'danger';
  });

  // ────────────────────────────────────────────────
  // Effects + Initialization
  // ────────────────────────────────────────────────
  constructor() {
    // Load config (required confirmations + poll interval)
    effect(() => {
      this.configService.getConfig()
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe(config => {
            this.paymentState.update(s => ({
              ...s,
              requiredConfirmations: config.web3?.requiredConfirmations ?? 21
            }));
            this.pollInterval.set(config.web3?.pollInterval ?? 15000);
          });
    });

    // Refresh the selected token's balance whenever the wallet, network or
    // token choice changes (connectionState().balance only tracks USDT).
    effect(() => {
      const state = this.connectionState();
      const token = this.selectedToken();
      if (state.connected && state.address && state.chainId === 56) {
        this.web3.getTokenBalance(state.address, token, state.chainId)
          .then(bal => this.tokenBalance.set(bal))
          .catch(() => this.tokenBalance.set('0'));
      } else {
        this.tokenBalance.set(null);
      }
    });

    // Auto-connect after render
    afterNextRender(async() => {
      // this.connectWallet();
      await this.web3.disconnectWallet();
    });
  }

  selectToken(token: 'USDT' | 'USDC'): void {
    if (this.tokenLocked()) return;
    this.selectedToken.set(token);
  }

  // ────────────────────────────────────────────────
  // Core logic
  // ────────────────────────────────────────────────
  async connectWallet(qrCode: boolean = false): Promise<void> {
    // this.logger.debug('connectWallet', {forceQR})
    this.qrCodeConnection.set(qrCode);
    this.loading.set(true);
    this.paymentState.update(s => ({ ...s, error: null }));
    try {
      let promise: Promise<Web3ConnectionState>;
      if (qrCode) {
        promise = this.web3.connectWalletConnect();
        this.loading.set(false);
      } else {
        promise = this.web3.connectWallet();
        setTimeout(() => {
          if (!this.connectionState().connected && this.connectionState().walletConnectUri) {
            this.loading.set(false);
          }
        }, 800);
      }
      const state = await promise;
      if (state.connected && state.address) {
        this.logEvent('connect', `Wallet connected (chainId=${state.chainId}, balance=${state.balance})`);

        if (this.orderId) {
          this.orderService.updateWeb3WalletAddress(this.orderId, state.address)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({ error: err => this.logger.error('Failed to store wallet address:', err) });
        }

        if (!this.isCorrectNetwork()) {
          this.paymentState.update(s => ({ ...s, message: this.translate.translate('web3_switch_network') }));
        } else {
          this.paymentState.update(s => ({
            ...s,
            message: this.translate.translate('web3_wallet_connected'),
            step: 'checking_balance'
          }));
        }
      }
    } catch (err: any) {
      this.paymentState.update(s => ({
        ...s,
        error: this.translate.translate('web3_connect_error', { error: err.message })
      }));
      this.logEvent('error', `Wallet connection failed: ${err.message}`);
      this.tracking?.step('payment_confirmation', 'error', 'other');
    } finally {
      this.loading.set(false);
    }
  }

  async disconnectWallet(): Promise<void> {
    await this.web3.disconnectWallet();
    this.paymentState.update(s => ({ ...s, step: 'connecting', message: '' }));
  }

  async switchNetwork(): Promise<void> {
    this.loading.set(true);
    try {
      await this.web3.switchToBSC();
      this.paymentState.update(s => ({ ...s, message: this.translate.translate('web3_switched_to_bsc') }));
    } catch (err: any) {
      this.paymentState.update(s => ({
        ...s,
        error: this.translate.translate('web3_switch_error', { error: err.message })
      }));
    } finally {
      this.loading.set(false);
    }
  }

  async sendPayment(): Promise<void> {
    if (!this.orderId || !this.connectionState().address) return;

    this.tracking?.step('payment_confirmation', 'submit');
    this.loading.set(true);
    this.paymentState.update(s => ({
      ...s,
      step: 'sending',
      error: null,
      message: this.translate.translate('web3_confirm_wallet')
    }));

    try {
      const token = this.selectedToken();
      const txHash = await this.web3.sendTokenPayment(this.recipientAddress, this.totalPrice, token);
      this.paymentState.update(s => ({
        ...s,
        transactionHash: txHash,
        message: this.translate.translate('web3_transaction_submitted')
      }));

      this.logEvent('send', `Tx sent: ${txHash}, amount=${this.totalPrice} ${token}`);

      // Submit transaction hash to backend → this triggers polling start
      this.orderService.submitWeb3Transaction(this.orderId, txHash, this.connectionState().address!, token)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: res => {
            this.paymentState.update(s => ({
              ...s,
              confirmations: res.confirmations || 0,
              step: 'confirming'
            }));

            // Start polling **only after backend submission succeeds**
            this.startPolling(txHash);
          },
          error: err => {
            const msg = err.error?.error || err.message;
            this.paymentState.update(s => ({
              ...s,
              error: this.translate.translate('web3_submit_error', { error: msg }),
              step: 'error'
            }));
            this.logEvent('error', `Backend submission failed: ${msg}`);
            this.tracking?.step('payment_confirmation', 'error', confirmationErrorCategory(err));
            this.web3.disconnectWallet();
          }
        });
    } catch (err: any) {
      const msg = err.message || 'Unknown error';
      this.paymentState.update(s => ({
        ...s,
        error: msg || this.translate.translate('web3_payment_error'),
        step: 'error'
      }));
      this.logEvent('error', `Tx failed: ${msg}`);
      // The wallet refused or failed the transfer (incl. the buyer rejecting it).
      this.tracking?.step('payment_confirmation', 'error', 'payment_declined');
      this.web3.disconnectWallet();
    } finally {
      this.loading.set(false);
    }
  }

  private startPolling(txHash: string): void {
    if (!this.orderId) return;

    interval(this.pollInterval())
      .pipe(
        switchMap(() => 
          this.orderService.getWeb3ConfirmationStatus(this.orderId!, txHash)
        ),
        takeWhile(response => response.status !== 'completed', true), // include final value
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: response => {
          this.paymentState.update(s => ({
            ...s,
            confirmations: response.confirmations || 0
          }));

          if (response.status === 'completed') {
            this.paymentState.update(s => ({
              ...s,
              step: 'completed',
              message: this.translate.translate('web3_payment_confirmed')
            }));

            this.logEvent('success', `Payment confirmed (${response.confirmations} conf)`);
            this.web3.disconnectWallet();

            setTimeout(() => {
              this.onSuccess.emit({
                orderId: this.orderId,
                transactionHash: txHash,
                isFirstOrder: response.is_first_order || false
              });
            }, 2000);
          }
        },
        error: err => {
          this.logger.error('Polling error:', err);
          // Do not stop polling on error — keep trying
        }
      });
  }

  // ────────────────────────────────────────────────
  // Helpers
  // ────────────────────────────────────────────────
  shortenAddress(address: string | null): string {
    if (!address) return '';
    return `${address.slice(0, 6)}...${address.slice(-4)}`;
  }

  getNetworkName(): string {
    const chainId = this.connectionState().chainId;
    return chainId === 56
      ? this.translate.translate('web3_network_bsc')
      : this.translate.translate('web3_network_unknown', { chainId });
  }

  openWalletConnectUri(uri: string): void {
    this.logger.debug('openWalletConnectUri', uri);
    if (!uri) return;
    if (this.platform.is('hybrid')) {
      const browser = this.inAppBrowser.create(uri, '_system');
      browser.on('exit')
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(() => browser.close());
    } else {
      const link = document.createElement('a');
      link.href = uri;
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }

    // Use universal WalletConnect deep link
    // Mobile wallets will intercept this and open automatically
    // const deepLink = `wc:${uri.replace('wc:', '')}`;
    // window.location.href = deepLink;
  }

  copyWalletConnectUri(): void {
    const uri = this.connectionState().walletConnectUri;
    if (!uri) return;

    navigator.clipboard.writeText(uri)
      .then(() => {
        this.paymentState.update(s => ({
          ...s,
          message: this.translate.translate('web3_uri_copied')
        }));
        setTimeout(() => this.paymentState.update(s => ({ ...s, message: '' })), 3000);
      })
      .catch(err => this.logger.error('Failed to copy URI:', err));
  }

  private logEvent(
    type: 'connect' | 'disconnect' | 'send' | 'success' | 'error',
    message: string
  ): void {
    if (!this.orderId) return;

    this.orderService.logWeb3Event(
      this.orderId,
      type,
      message,
      this.connectionState().address ?? undefined,
      this.paymentState().transactionHash ?? undefined
    )
    .pipe(takeUntilDestroyed(this.destroyRef))
    .subscribe({
      error: err => this.logger.error('Failed to log web3 event:', err)
    });
  }
}